import React from 'react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Input, Button, Form } from 'semantic-ui-react';
import { imageUrl } from '../../helpers';
import moment from 'moment';
import { formatMoneyLegacy } from '../../utils/currency';
import getDepartureCardCopy from '../../utils/getDepartureCardCopy';

function formSchema(spotsLeft) {
  const phone = yup.string()
    .required('Phone is required')
    .transform((v) => (typeof v === 'string' ? v.replace(/\s/g, '') : v))
    .matches(/^\+?[1-9][0-9]{9,14}$/, 'Valid phone with country code (e.g. +9779812345678), no spaces');

  let numPeople = yup.number()
    .transform((v, o) => (o === '' || v === '' || v == null ? 1 : v))
    .min(1, 'At least 1')
    .integer('Must be a whole number');
  if (spotsLeft != null && spotsLeft >= 0) {
    numPeople = numPeople.max(spotsLeft, `Maximum ${spotsLeft} (only that many spots left)`);
  }

  return yup.object().shape({
    name: yup.string().required('Name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone,
    number_of_people: numPeople,
    message: yup.string()
  });
}

function formatDates(t) {
  if (t.start_date && t.end_date) return `${moment(t.start_date).format('D MMM YYYY')} – ${moment(t.end_date).format('D MMM YYYY')}`;
  if (t.start_date) return `From ${moment(t.start_date).format('D MMM YYYY')}`;
  if (t.end_date) return `Until ${moment(t.end_date).format('D MMM YYYY')}`;
  return t.preferred_dates || '';
}

export default function GroupTourCard({ tour, variant = 'public', onSelect, onDelete, isSelected, onSubmitInterest, submitting, deleteDisabled, t, currentUser, visibilityState: visibilityStateProp, usdToNprRate }) {
  const fromTo = `${tour.origin || '—'} → ${tour.destination || '—'}`;
  const dates = formatDates(tour);
  const people = tour.paid_people_count != null ? tour.paid_people_count : (tour.paid_interests_count != null ? tour.paid_interests_count : 0);
  const regs = tour.paid_interests_count != null ? tour.paid_interests_count : 0;
  const peopleLabel = (regs !== people && regs > 0) ? `${people} (${regs} reg.)` : `${people}`;
  const min = tour.min_group_size != null ? tour.min_group_size : 0;
  const travellerCopy = getDepartureCardCopy(tour);
  const formed = travellerCopy.kind === 'guaranteed' || !!tour.group_formed;
  const rest = (tour.rest_amount != null && tour.rest_amount > 0) ? tour.rest_amount : null;
  const img = tour.images && tour.images[0] && (tour.images[0].url || tour.images[0]);
  const Tl = t || ((k) => k);
  const cc = tour.currency_code || 'NPR';
  const interestAmount = Number(tour.interest_amount) || 0;
  const restAmount = (rest != null && rest > 0) ? Number(rest) : 0;
  const totalAmount = interestAmount + restAmount;
  const interestPct = (totalAmount > 0 && interestAmount > 0 && restAmount > 0) ? Math.round((interestAmount / totalAmount) * 100) : null;
  const remainingPct = interestPct != null ? (100 - interestPct) : null;
  const nprEquivalent = (cc.toUpperCase() === 'USD' && usdToNprRate && interestAmount > 0) ? Math.round(interestAmount * usdToNprRate * 100) / 100 : null;
  const usdEquivalent = (cc.toUpperCase() === 'NPR' && usdToNprRate && interestAmount > 0) ? Math.round((interestAmount / usdToNprRate) * 100) / 100 : null;

  // —— Admin: unchanged card-widget layout ——
  if (variant === 'admin') {
    const cardContent = (
      <>
        <figure style={{ position: 'relative' }}>
          {img ? (<img src={imageUrl(typeof img === 'string' ? img : img.url)} alt={tour.name} className="img-responsive" />) : (<div className="placeholder-tour"><i className="fas fa-route" aria-hidden="true" /></div>)}
          {tour.accepting_registrations === false && <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(100,116,139,0.95)', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>Full</div>}
          {formed && tour.accepting_registrations !== false && <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(40,167,69,0.95)', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>✓ Group formed</div>}
        </figure>
        <div className="details">
          <h3>{tour.name}</h3>
          <div className="d-flex justify-content-between align-items-center py-2 flex-wrap"><span className="text-small text-muted"><i className="fas fa-map-marker-alt" />&nbsp;{fromTo}</span></div>
          {dates && <div className="text-small text-muted mb-1"><i className="fas fa-calendar-alt" />&nbsp;{dates}</div>}
          <div className="d-flex justify-content-between align-items-center py-1"><span className="text-small">{formatMoneyLegacy(tour.interest_amount, cc)} interest</span>{rest != null && <span className="text-small text-muted">{formatMoneyLegacy(rest, cc)} rest</span>}</div>
          <div className="text-small text-muted py-1"><i className="fas fa-users" />&nbsp;{peopleLabel}{min ? ` / ${min} min` : ''}</div>
          <div className="mt-2 pt-2 border-top" onClick={(e) => e.stopPropagation()}>
            <span className={`badge ${tour.published ? 'badge-success' : 'badge-secondary'} mr-2`}>{tour.published ? 'Published' : 'Unpublished'}</span>
            <div className="d-flex mt-1">
              <Link to={{ pathname: `/admin/group_tour_details/${tour.idx}`, state: { groupTour: tour } }} className="btn btn-sm btn-outline-primary mr-1">View</Link>
              {tour.has_any_booking ? (
                <span className="text-muted small align-self-center mr-1" title="Not available once anyone has registered">Edit/Delete locked</span>
              ) : (
                <>
                  <Link to={{ pathname: `/admin/group_tour_form/${tour.idx}`, state: { groupTour: tour } }} className="btn btn-sm btn-outline-secondary mr-1">Edit</Link>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDelete && onDelete(tour.idx)} disabled={!!deleteDisabled}>Delete</button>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
    return <div className="card-widget">{cardContent}</div>;
  }

  // —— Public: use centralized visibility state when provided ——
  const showForm = !!(isSelected && onSubmitInterest);
  const canRegister = visibilityStateProp ? !visibilityStateProp.disabled : (tour.accepting_registrations !== false && !(tour.spots_left != null && tour.spots_left <= 0));
  const isCutoff = visibilityStateProp?.reason === 'cutoff';
  const isFull = visibilityStateProp?.reason === 'full';
  const slotFull = tour.spots_left != null && tour.spots_left <= 0;

  return (
    <div className={`group-tour-card ${showForm ? 'group-tour-card--open' : ''} ${!canRegister ? 'group-tour-card--full' : ''}`}>
      <div
        className="group-tour-card__summary"
        onClick={() => onSelect && onSelect(tour)}
        role={canRegister ? 'button' : undefined}
        tabIndex={canRegister ? 0 : undefined}
        onKeyDown={canRegister ? (e) => { if (e.key === 'Enter' || e.key === ' ') onSelect && onSelect(tour); } : undefined}
      >
        <div className="group-tour-card__head">
          {img ? (
            <img src={imageUrl(typeof img === 'string' ? img : img.url)} alt={tour.name} />
          ) : (
            <div className="group-tour-card__placeholder"><i className="fas fa-route" /></div>
          )}
          {formed && <span className="group-tour-card__badge">{travellerCopy.statusLabel}</span>}
          {isCutoff && (
            <span className="group-tour-card__slot-badge group-tour-card__slot-badge--closed">Registration closed</span>
          )}
          {isFull && (
            <span className="group-tour-card__slot-badge group-tour-card__slot-badge--full">SLOT FULL</span>
          )}
          {canRegister && travellerCopy.showCapacity && tour.seats_remaining != null && tour.seats_remaining > 0 && tour.seats_remaining <= 3 && (
            <span className="group-tour-card__slot-badge group-tour-card__slot-badge--seats">
              Only {tour.seats_remaining} {tour.seats_remaining === 1 ? 'seat' : 'seats'} left
            </span>
          )}
        </div>
        <div className="group-tour-card__body">
          <h4 className="group-tour-card__title">{tour.name}</h4>
          <div className="group-tour-card__meta"><i className="fas fa-map-marker-alt" /> {fromTo}</div>
          {dates && <div className="group-tour-card__meta"><i className="fas fa-calendar-alt" /> {dates}</div>}
          <div className="group-tour-card__traveller-status">
            <p className={`group-tour-card__status-pill group-tour-card__status-pill--${travellerCopy.kind}`}>{travellerCopy.statusLabel}</p>
            <p className="group-tour-card__status-story">{travellerCopy.guaranteeMessage}</p>
            {travellerCopy.showCapacity && travellerCopy.capacityMessage && (
              <p className="group-tour-card__status-capacity">{travellerCopy.capacityMessage}</p>
            )}
          </div>
          {!showForm && (
            <div className="group-tour-card__payment">
              <p className="group-tour-card__total">{formatMoneyLegacy(totalAmount, cc)} per person</p>
              {canRegister ? (
                <>
                  <p className="group-tour-card__reserve">
                    Reserve with{' '}
                    {cc.toUpperCase() === 'USD' && nprEquivalent != null
                      ? <>{formatMoneyLegacy(tour.interest_amount, 'USD')} (≈ Rs {nprEquivalent.toLocaleString('en-IN', { maximumFractionDigits: 0 })})</>
                      : cc.toUpperCase() === 'NPR' && usdEquivalent != null
                        ? <>${usdEquivalent} (≈ {formatMoneyLegacy(tour.interest_amount, 'NPR')})</>
                        : <>{formatMoneyLegacy(tour.interest_amount, cc)}</>}
                  </p>
                  {remainingPct != null && (
                    <p className="group-tour-card__remaining">Remaining {remainingPct}% due after group confirmation.</p>
                  )}
                  <span className="group-tour-card__cta group-tour-card__cta--button">{travellerCopy.reserveCta}</span>
                </>
              ) : (
                <>
                  <p className="group-tour-card__payment-status">{isCutoff ? (visibilityStateProp?.label || 'Registration closed') : 'Group full'}</p>
                  <span className="group-tour-card__cta group-tour-card__cta--button group-tour-card__cta--disabled" aria-disabled="true">
                    {isCutoff ? 'Registration Closed' : 'Slot Full'}
                  </span>
                </>
              )}
              <p className="group-tour-card__payment-methods">Pay via Khalti · eSewa · Visa/Mastercard</p>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="group-tour-card__form-wrap group-tour-card__form-wrap--open">
          <div className="group-tour-card__form" onClick={(e) => e.stopPropagation()}>
            <div className="group-tour-card__form-head">
              <span>Reserve your spot — {tour.name}</span>
              <button type="button" className="group-tour-card__close" onClick={() => onSelect && onSelect(tour)} aria-label="Close">×</button>
            </div>
            <Formik
              initialValues={{
                name: currentUser?.name || '',
                email: currentUser?.email || '',
                phone: (currentUser?.phone_number || '').replace(/\s/g, ''),
                number_of_people: 1,
                message: ''
              }}
              validationSchema={formSchema(tour.spots_left)}
              onSubmit={async (values, { setStatus }) => {
                setStatus('');
                const toSend = {
                  ...values,
                  phone: (values.phone || '').replace(/\s/g, ''),
                  number_of_people: Math.max(1, (Number(values.number_of_people) || 1) | 0)
                };
                try {
                  if (onSubmitInterest) await onSubmitInterest(tour, toSend);
                } catch (e) {
                  const msg = (e?.response?.data?.errors || []).join(' ') || e?.message || 'Something went wrong.';
                  setStatus(msg);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setStatus, status, isSubmitting }) => (
                <form onSubmit={handleSubmit} className="group-tour-card__form-fields">
                  {status && <div className="group-tour-card__err group-tour-card__err--form" role="alert">{status}</div>}
                  <div className="group-tour-card__field">
                    <label>{Tl('Name')} *</label>
                    <Input size="small" name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} className="group-tour-card__input" />
                    {touched.name && errors.name && <span className="group-tour-card__err">{errors.name}</span>}
                  </div>
                  <div className="group-tour-card__field">
                    <label>{Tl('Email')} * <span className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.85em' }}>(your account — not editable)</span></label>
                    <Input size="small" type="email" name="email" value={values.email} readOnly className="group-tour-card__input" style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }} />
                    {touched.email && errors.email && <span className="group-tour-card__err">{errors.email}</span>}
                  </div>
                  <div className="group-tour-card__field">
                    <label>{Tl('Phone')} * <span className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.85em' }}>(with country code, e.g. +9779812345678, no spaces)</span></label>
                    <Input
                      size="small"
                      name="phone"
                      value={values.phone}
                      onChange={(e) => { const v = (e.target.value || '').replace(/\s/g, ''); setFieldValue('phone', v); }}
                      onBlur={handleBlur}
                      placeholder="+9779812345678"
                      className="group-tour-card__input"
                    />
                    {touched.phone && errors.phone && <span className="group-tour-card__err">{errors.phone}</span>}
                  </div>
                  <div className="group-tour-card__field">
                    <label>{Tl('Number of people')} *{tour.spots_left != null ? ` (${tour.spots_left} spots left)` : ''}</label>
                    <Input size="small" type="number" min={1} max={tour.spots_left != null ? tour.spots_left : undefined} name="number_of_people" value={values.number_of_people} onChange={(e) => { handleChange(e); if (status) setStatus(''); }} onBlur={handleBlur} className="group-tour-card__input" disabled={slotFull} />
                    {touched.number_of_people && errors.number_of_people && <span className="group-tour-card__err">{errors.number_of_people}</span>}
                  </div>
                  <div className="group-tour-card__field">
                    <label>{Tl('Message')}</label>
                    <Form.TextArea name="message" value={values.message} onChange={(e, { value }) => setFieldValue('message', value)} onBlur={handleBlur} rows={2} className="group-tour-card__input" />
                  </div>
                  <div className="group-tour-card__form-info" role="region" aria-label="Refund and payment info">
                    <p className="group-tour-card__form-info__refund">{interestPct != null ? `${interestPct}%` : 'Interest'} is refunded if the group does not form. Once the group is confirmed, payment is non-refundable if you cancel.</p>
                    <p className="group-tour-card__form-info__methods">Pay via Khalti · eSewa · Visa/Mastercard</p>
                    {cc.toUpperCase() === 'USD' && nprEquivalent != null && (
                      <p className="group-tour-card__form-info__rate"><i className="fas fa-lock" aria-hidden="true" /> Fixed conversion rate at checkout</p>
                    )}
                  </div>
                  {slotFull ? (
                    <p className="group-tour-card__err" style={{ marginTop: '0.5rem', marginBottom: 0 }}>This tour is full. Registration is closed.</p>
                  ) : (
                    <Button type="submit" primary disabled={!!submitting || !!isSubmitting} className="group-tour-card__submit">
                      {cc.toUpperCase() === 'USD' && nprEquivalent != null
                        ? `Pay ${formatMoneyLegacy(Number(tour.interest_amount || 0) * (Number(values.number_of_people) || 1), 'USD')} (≈ Rs ${(nprEquivalent * (Number(values.number_of_people) || 1)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}) to show interest`
                        : cc.toUpperCase() === 'NPR' && usdEquivalent != null
                          ? `Pay $${(usdEquivalent * (Number(values.number_of_people) || 1)).toFixed(2)} (≈ ${formatMoneyLegacy(Number(tour.interest_amount || 0) * (Number(values.number_of_people) || 1), 'NPR')}) to show interest`
                          : `Pay ${formatMoneyLegacy(Number(tour.interest_amount || 0) * (Number(values.number_of_people) || 1), cc)} to show interest`}
                    </Button>
                  )}
                </form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
}
